import type { CalorieStat } from "src/state/MealsStatsCubit/MealsStatsCubit";

/**
 * Eating occasions the daily insight is gated behind (BACK-5890). The stats
 * endpoint still returns encouragement copy on a thin day — it only omits
 * `insights` when generation fails outright — so the gate has to be applied here
 * rather than inferred from the text being absent.
 */
export const DAILY_INSIGHT_MEAL_THRESHOLD = 2;

export type DailyInsightState = "intro" | "locked" | "unlocked";

export interface DailyInsightInput {
  /** Whether the member has ever logged a meal, on any day. */
  hasAnyMeals: boolean;
  /** Today's insight text from the stats endpoint; empty when none was returned. */
  insights: string;
  /** Meals logged today. */
  mealCount: number;
}

/**
 * Which of the three daily insight cards the Meals tab shows. Presence of
 * `insights` is not enough on its own: the stats endpoint writes encouragement
 * copy for a day with one meal or none, and omits the text only when generation
 * failed — so the meal count has to agree before the insight counts as unlocked.
 */
export const dailyInsightState = ({
  hasAnyMeals,
  insights,
  mealCount
}: DailyInsightInput): DailyInsightState => {
  if (!hasAnyMeals) {
    return "intro";
  }
  const unlocked =
    Boolean(insights) && mealCount >= DAILY_INSIGHT_MEAL_THRESHOLD;
  return unlocked ? "unlocked" : "locked";
};

/** Meals logged today, capped so the unlock hint can never read past its target. */
export const unlockProgress = (mealCount: number): number =>
  Math.min(Math.max(mealCount, 0), DAILY_INSIGHT_MEAL_THRESHOLD);

export const formatCalorieRange = (
  from?: number,
  to?: number
): string | undefined => {
  if (from && to) {
    return `${from}-${to} kcal`;
  }
  if (from) {
    return `${from}+ kcal`;
  }
  return undefined;
};

export const formatNutrition = (value?: number, unit = "g"): string => {
  return value ? `${Math.round(value)}${unit}` : "--";
};

/**
 * How far past the calorie target the day's estimate has to land before the bar
 * flags it. Under this allowance the estimate's own error bars are wider than
 * the overage, so it is not worth calling out.
 */
export const CALORIE_OVERSHOOT_ALLOWANCE_KCAL = 100;

/**
 * True once today's calorie estimate is materially — not marginally — above the
 * member's target, which is when the bar grows its overshoot segment.
 */
export const isCalorieOvershoot = ({ total, target }: CalorieStat): boolean =>
  target > 0 && total > target + CALORIE_OVERSHOOT_ALLOWANCE_KCAL;
